import PropTypes from 'prop-types';
import { useContext } from 'react';

import { StateContext } from '@/context/index';

import { isWHEnv, isDDEnv } from '@/utils/helper';

import Icon from '@/atoms/Icon/Icon';
import Text from '@/atoms/Text/Text';

import { normalizedUrl } from './CTA.constants';
import { ICON_POSITION, getIconClass } from '@/atoms/Icon/Icon.constants';
import { getButtonClass } from '@/atoms/Button/Button.constants';
import { CtfCtaStyle, localeConfig } from '@/utils/constants';
import { useRouter } from 'next/router';

const localizeUrl = (url, asPath) => {
  const currentLocale = localeConfig.locales.find(
    l => asPath.startsWith(`/${l}/`) || asPath === `/${l}`
  );
  const activeLocale = currentLocale !== localeConfig.defaultLocale ? currentLocale : null;

  if (!activeLocale) return url;

  const urlHasLocale = localeConfig.locales.some(
    l => url.startsWith(`/${l}/`) || url === `/${l}`
  );

  if (urlHasLocale) return url;

  return `/${activeLocale}${url}`;
};

export function CtaWrapper({ url, page, children, classNames, name, onClick, animation }) {
  const { locale, asPath } = useRouter();
  const { environment } = useContext(StateContext);
  const isLocal = (urlText) =>
    !urlText.startsWith('#') && !urlText.startsWith('http') && !urlText.startsWith('mailto:') && !urlText.startsWith('tel:')
  const isLocalUrl = isLocal(`${url ?? page}`)
  let newUrl = `${page ?? url}`
  if (isLocalUrl) {
    newUrl = normalizedUrl(`${page ?? url}`);
    if (environment) {
      newUrl = `/${environment}${newUrl}`;
    }

    // Force full page reload instead of client-side navigation
    return (
      <a
        href={localizeUrl(newUrl, asPath)}
        className={`aaco-cta inline-flex items-center ${classNames ?? ''}`}
        onClick={(e) => {
          if (onClick) {
            onClick(e, { localUrl: newUrl, name, animation, locale });
          }
        }}
      >
        {children}
      </a>
    );
  }

  const anchor = url?.startsWith('#');
  return (
    <a
      className={`aaco-cta flex items-center ${classNames ?? ''}`}
      href={url}
      target={anchor ? '_self' : ''}
      rel="noopener noreferrer"
      onClick={(e) => {
        if (onClick) {
          onClick(e, { url: !anchor && url, localUrl: anchor && url, name, animation, locale });
        }
      }}
    >
      {children}
    </a>
  );
}

export function Cta({
  iconProps = {},
  buttonProps = {},
  linkProps = {},
  onClick,
  animation,
} = {}) {
  const { invert = false, name, type = 'Primary', style: buttonStyle, size = "large", wrapperClassName } = buttonProps;
  const { url, page } = linkProps;

  const {
    hasIconBorder,
    iconPosition = ICON_POSITION.right,
    height,
    hideIcon,
    name: icon,
    style: iconStyle = '',
    width,
  } = iconProps;

  const iconButtonClass = getIconClass({
    icon,
    style: iconStyle,
    iconPosition,
    invert,
    hasIconBorder: isWHEnv() ? true : hasIconBorder,
  });

  const iconTextClass = getButtonClass({
    type,
    invert,
    style: buttonStyle,
  });

  const isDD = isDDEnv();

  const getCtaWrapperClass = () => {
    let typeClass = '';
    switch (type) {
      case CtfCtaStyle.DD_PRIMARY_FILLED:
        typeClass = 'aaco-cta__primary-filled';
        break;
      case CtfCtaStyle.DD_PRIMARY_LINE:
        typeClass = 'aaco-cta__primary-line';
        break;
      case CtfCtaStyle.DD_SECONDARY:
        typeClass = 'aaco-cta__secondary';
        break;
      case CtfCtaStyle.DD_TERTIARY:
        typeClass = 'aaco-cta__tertiary';
        break;
      default:
        break;
    }
    const invertClass = invert ? 'aaco-cta--invert link-invert' : '';
    return [typeClass, invertClass, wrapperClassName].join(' ');
  };

  const showIcon = !hideIcon && icon;
  const textSize = 'button';
  return (
    <CtaWrapper
      url={url}
      page={page}
      classNames={getCtaWrapperClass()}
      onClick={onClick}
      name={name}
      animation={animation}
    >
      {isDDEnv() && showIcon && iconPosition === ICON_POSITION.left && (
        <div className={iconButtonClass}>
          <Icon
            name={icon}
            className={iconStyle}
            height={height}
            width={width}
            hasIconBorder={hasIconBorder}
          />
        </div>
      )}
      {showIcon ? (
        <div className="aaco-underline">
          <Text tag={textSize} style={iconTextClass}>
            {name}
          </Text>
        </div>
      ) : (
        <Text tag={textSize} style={iconTextClass}>
          {name}
        </Text>
      )}
      {isDDEnv() && showIcon && iconPosition === ICON_POSITION.right && (
        <div className={iconButtonClass}>
          <Icon
            name={icon}
            className={iconStyle}
            width={width}
            height={height}
            hasIconBorder={hasIconBorder}
          />
        </div>
      )}
    </CtaWrapper>
  );
}

Cta.propTypes = {
  url: PropTypes.string,
  height: PropTypes.number,
  page: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  icon: PropTypes.string,
  iconStyle: PropTypes.string,
  iconPosition: PropTypes.string,
  invert: PropTypes.bool,
  style: PropTypes.string,
  width: PropTypes.number,
  onClick: PropTypes.func,
};
